import { useEffect, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { ImportWorkerDraftDetailResponse } from "@/features/hr/employees/types";
import type { ContractTab, DocumentTab, ImportWorkerFormValues, ImportWorkerSectionId } from "../types";
import { DEFAULT_FORM_VALUES } from "../consts";

const DRAFT_KEY_MIGRATION: Record<string, string> = {
  no_education: "has_no_education",
  no_experience: "has_no_work_experience",
};

const DATE_FIELDS = new Set([
  "date_of_birth",
  "sign_date",
  "start_date",
  "enrollment_start_date",
  "enrollment_end_date",
]);

interface Options {
  draftData: ImportWorkerDraftDetailResponse | undefined;
  methods: UseFormReturn<ImportWorkerFormValues>;
  setActiveSection: (section: ImportWorkerSectionId) => void;
  setContractTab: (tab: ContractTab) => void;
  setDocumentsTab: (tab: DocumentTab) => void;
}

export function useImportDraftRestoration({
  draftData,
  methods,
  setActiveSection,
  setContractTab,
  setDocumentsTab,
}: Options): { draftRestoredRef: React.MutableRefObject<boolean> } {
  const draftRestoredRef = useRef(false);

  useEffect(
    function restoreDraft() {
      if (!draftData || draftRestoredRef.current) return;
      draftRestoredRef.current = true;
      const sd = draftData.scalar_data;
      if (!sd) return;
      const restored: Partial<ImportWorkerFormValues> = {};
      for (const [key, value] of Object.entries(sd)) {
        const mappedKey = DRAFT_KEY_MIGRATION[key] ?? key;
        if (DATE_FIELDS.has(mappedKey)) {
          (restored as Record<string, unknown>)[mappedKey] = value ? new Date(value as string) : null;
        } else {
          (restored as Record<string, unknown>)[mappedKey] = value;
        }
      }
      methods.reset({ ...DEFAULT_FORM_VALUES, ...restored });
      if (draftData.last_section) {
        setActiveSection(draftData.last_section as ImportWorkerSectionId);
      }
      if (draftData.last_contract_tab) {
        setContractTab(draftData.last_contract_tab as ContractTab);
      }
      if (draftData.last_documents_tab) {
        setDocumentsTab(draftData.last_documents_tab as DocumentTab);
      }
    },
    [draftData],
  );

  return { draftRestoredRef };
}
